'use client'

import { useState, useEffect, useCallback } from 'react'
import { ArrowUp } from 'lucide-react'
import ReadingProgress from './ReadingProgress'

interface ArticleClientProps {
  children: React.ReactNode
  /** Total estimated reading time in minutes */
  readingTime: number
}

export default function ArticleClient({ children, readingTime }: ArticleClientProps) {
  const [showScrollTop, setShowScrollTop] = useState(false)

  const handleScroll = useCallback(() => {
    const docHeight = document.documentElement.scrollHeight - window.innerHeight
    const progress = docHeight > 0 ? window.scrollY / docHeight : 0
    // Show scroll-to-top button after 30% scroll
    setShowScrollTop(progress > 0.3)
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <>
      {/* Reading Progress (bar + time pill) */}
      <ReadingProgress totalMinutes={readingTime} />

      {/* Page Content with fade-in entrance */}
      <div className="anim-fade-in-up in-view">
        {children}
      </div>

      {/* Scroll-to-top FAB — positioned above the reading time pill */}
      <button
        onClick={scrollToTop}
        className="fixed z-40 cursor-pointer"
        style={{
          bottom: '4.5rem',
          right: '1.5rem',
          width: '2.75rem',
          height: '2.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(18, 18, 18, 0.9) 0%, rgba(10, 10, 10, 0.85) 100%)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 20px rgba(220, 38, 38, 0.06)',
          color: '#A1A1AA',
          opacity: showScrollTop ? 1 : 0,
          transform: showScrollTop ? 'scale(1)' : 'scale(0.6)',
          transition: 'opacity 0.3s ease, transform 0.3s ease, color 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
          pointerEvents: showScrollTop ? 'auto' : 'none',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = '#EF4444'
          e.currentTarget.style.borderColor = 'rgba(220, 38, 38, 0.3)'
          e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 24px rgba(220, 38, 38, 0.15)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = '#A1A1AA'
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)'
          e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 20px rgba(220, 38, 38, 0.06)'
        }}
        aria-label="Scroll to top"
      >
        <ArrowUp size={18} />
      </button>
    </>
  )
}
